"use client";

import { FactoryBillingRepo } from "@/core/repos/tables/factory-billing.repo";
import container from "@/lib/di/container";
import { useEffect, useState } from "react";
import { Alert, Button, Card, Form, InputNumber, Spin, Switch, Typography, message } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { useTitleDataStore } from "@/core/stores/title-data.store";

const factoryBillingRepo = container.get(FactoryBillingRepo);

const { Title, Paragraph, Text } = Typography;

export default function EInvoiceNotificationSettingPage() {
    const titleData = useTitleDataStore();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [day, setDay] = useState<number | null>(10);
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        titleData.setTitleText("Cấu hình ngày gửi thông báo HĐĐT");
        loadSetting();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadSetting = async () => {
        setLoading(true);
        try {
            const billing = await factoryBillingRepo.GetByWaterFactory();
            const configuredDay = billing?.eInvoiceNotificationDay;
            if (configuredDay && configuredDay > 0) {
                setEnabled(true);
                setDay(configuredDay);
            } else {
                setEnabled(false);
                setDay(10);
            }
        } catch (err) {
            console.log(err);
            messageApi.error("Không tải được cấu hình nhà máy");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (enabled && (!day || day < 1 || day > 28)) {
            messageApi.warning("Ngày gửi thông báo phải từ 1 đến 28");
            return;
        }

        setSaving(true);
        try {
            const result = await factoryBillingRepo.updateEInvoiceNotificationDay(
                enabled ? day : null
            );
            if (result.success) {
                messageApi.success(
                    enabled
                        ? `Đã cấu hình gửi thông báo HĐĐT vào ngày ${day} hàng tháng`
                        : "Đã tắt lịch gửi — hệ thống sẽ gửi ngay khi phát hành HĐĐT"
                );
                await loadSetting();
            } else {
                messageApi.error(result.message || "Cập nhật thất bại");
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4">
            {contextHolder}
            <Spin spinning={loading}>
                <Card>
                    <Title level={4}>Cấu hình ngày gửi thông báo hoá đơn điện tử</Title>
                    <Paragraph type="secondary">
                        Khi bật, hệ thống không gửi thông báo ngay lúc phát hành HĐĐT.
                        Vào ngày đã chọn mỗi tháng, hệ thống gửi thông báo cho khách hàng
                        có hoá đơn của <Text strong>tháng trước</Text> (đã phát hành HĐĐT).
                        Nếu HĐĐT phát hành muộn hơn ngày cấu hình, hệ thống vẫn gửi (catch-up)
                        trong các ngày còn lại của tháng.
                    </Paragraph>

                    <Alert
                        className="mb-4"
                        type="info"
                        showIcon
                        message="Ví dụ"
                        description="Set ngày 10: vào ngày 10/7 trở đi, gửi noti cho hoá đơn tháng 6 đã có HĐĐT nhưng chưa gửi thông báo."
                    />

                    <Form layout="vertical" style={{ maxWidth: 420 }}>
                        <Form.Item label="Bật lịch gửi thông báo theo ngày">
                            <Switch
                                checked={enabled}
                                onChange={(checked) => setEnabled(checked)}
                                checkedChildren="Bật"
                                unCheckedChildren="Tắt"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Ngày gửi trong tháng (1–28)"
                            extra={
                                enabled
                                    ? "Ngày trong tháng hệ thống bắt đầu gửi thông báo HĐĐT tháng trước"
                                    : "Tắt lịch = gửi ngay khi webhook phát hành HĐĐT (cách cũ)"
                            }
                        >
                            <InputNumber
                                min={1}
                                max={28}
                                value={day ?? undefined}
                                disabled={!enabled}
                                onChange={(value) => setDay(value)}
                                style={{ width: "100%" }}
                            />
                        </Form.Item>

                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={saving}
                            onClick={handleSave}
                        >
                            Lưu cấu hình
                        </Button>
                    </Form>
                </Card>
            </Spin>
        </div>
    );
}
